import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { UrgencyBadge, TypeBadge, urgencyBorder } from "@/components/Badges";
import { cn } from "@/lib/utils";
import { BellRing, CalendarClock } from "lucide-react";

export default function Alerts() {
  const { t } = useI18n();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });

  if (isLoading || !data) {
    return <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-renco-gold border-t-transparent" /></div>;
  }

  // Timeline: expired first, then upcoming ascending
  const timeline = [...data.expired, ...data.upcoming];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("alerts")}</h1>
        <p className="mt-1 text-muted-foreground">{t("overviewSub")}</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[
          { k: "expired", v: data.buckets.expired },
          { k: "critical", v: data.buckets.critical },
          { k: "warning", v: data.buckets.warning },
          { k: "safe", v: data.buckets.safe },
        ].map((x) => (
          <Card key={x.k} className={cn("border-l-4 p-4", urgencyBorder[x.k])} data-testid={`alert-count-${x.k}`}>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t(x.k)}</div>
            <div className="mt-1 font-display text-2xl font-bold">{x.v}</div>
          </Card>
        ))}
      </div>

      <Card className="p-5" data-testid="alerts-timeline">
        <div className="mb-5 flex items-center gap-2">
          <BellRing className="h-5 w-5 text-renco-gold" />
          <h3 className="font-display text-lg font-semibold">{t("upcomingRenewals")}</h3>
        </div>

        {timeline.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t("noUpcoming")}</p>
        ) : (
          <ol className="relative space-y-4 border-l-2 border-dashed border-border pl-6">
            {timeline.map((d) => {
              const overdue = d.days_to_expiry < 0;
              return (
                <li key={d.id} data-testid={`alert-item-${d.id}`} className="relative animate-fade-up">
                  <span className={cn("absolute -left-[31px] flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-background", d.urgency === "expired" ? "bg-rose-600" : d.urgency === "critical" ? "bg-amber-500" : d.urgency === "warning" ? "bg-yellow-400" : "bg-emerald-500")} />
                  <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card px-4 py-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 font-medium">
                        <CalendarClock className="h-4 w-4 text-muted-foreground" />
                        {d.title}
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                        {d.company_name} · <TypeBadge type={d.type} /> · <span className="font-mono">{d.expiry_date}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={cn("text-sm font-semibold", overdue ? "text-rose-600" : "text-muted-foreground")}>
                        {Math.abs(d.days_to_expiry)} {overdue ? t("daysOverdue") : t("daysLeft")}
                      </span>
                      <UrgencyBadge urgency={d.urgency} />
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </Card>
    </div>
  );
}
